import React from 'react';
import styles from '../BranchDrawer.module.css';
import Ico from '../../common/Ico';
import Pill from '../../common/Pill';
import { statusLabel, STATUS_KINDS } from '../statusConfig';
import { ICO_ALERT, ICO_CLOUD, ICO_EXTERNAL, ICO_LAYERS } from './constants';

// Segment order on the progress track, left to right
function breakdown(counts, finished) {
  return [
    { key: 'done',    label: 'Upgraded',  kind: 'ok' },
    { key: 'running', label: 'Upgrading', kind: 'info' },
    { key: 'failed',  label: 'Failed',    kind: 'err' },
    { key: 'skipped', label: 'Skipped',   kind: 'neutral' },
    // Once finished, pending clusters were stopped by a failure in their group, not waiting
    { key: 'pending', label: finished ? 'Not attempted' : 'Queued', kind: finished ? 'neutral' : 'warn' },
  ].map(s => ({ ...s, value: counts[s.key] || 0 }));
}

function Note({ error, children }) {
  return (
    <div className={error ? styles.ftNoteError : styles.ftNote} role={error ? 'alert' : undefined}>
      {error && <Ico d={ICO_ALERT} size={14} strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 1 }} />}
      <span>{children}</span>
    </div>
  );
}

export default function FreeTierRolloutCard({ build }) {
  const rollout = build.free_tier_rollout;
  if (!rollout?.status) return null;

  const counts    = rollout.counts || {};
  const hasCounts = rollout.counts !== undefined;
  const total     = counts.total || 0;
  const finished  = !!rollout.finished;
  const startFailed = rollout.status === 'failed' && !finished;
  const segments  = breakdown(counts, finished);
  const notAttempted = finished ? counts.pending || 0 : 0;

  let note = null;
  if (startFailed) {
    note = (
      <Note error>
        Couldn't start the rollout{rollout.error ? `: ${rollout.error.replace(/\.?$/, '.')}` : '.'} Nothing was upgraded. Use Retry free tier to try again.
      </Note>
    );
  } else if (!hasCounts) {
    note = <Note>Waiting for the first progress update from Collate free-tier.</Note>;
  } else if (finished && total === 0) {
    note = <Note>Every running free-tier cluster was already on this build.</Note>;
  } else if (notAttempted > 0) {
    note = (
      <Note>
        A failed upgrade stops the rest of its customer group, so {notAttempted} {notAttempted === 1 ? 'cluster was' : 'clusters were'} not attempted.
        Retry the failed task in Collate free-tier to resume them.
      </Note>
    );
  }

  return (
    <div className={styles.povCard}>
      <div className={styles.povCardHead}>
        <span className={styles.povIconTile}>
          <Ico d={ICO_CLOUD} size={19} strokeWidth={1.7} />
        </span>
        <div className={styles.povTitleCol}>
          <div className={styles.povTitleRow}>
            <span className={styles.povTitle}>Free tier</span>
            {build.build_tag && <span className={styles.povVersionBadge}>{build.build_tag}</span>}
            <span className={styles.povManaged}>
              <Ico d={ICO_LAYERS} size={11} strokeWidth={1.8} />
              Managed in Collate free-tier
            </span>
          </div>
          {total > 0 && (
            <div className={styles.povProgressRow}>
              <div
                className={styles.povProgressTrack}
                role="img"
                aria-label={segments.map(s => `${s.value} ${s.label.toLowerCase()}`).join(', ')}
              >
                <div className={styles.ftSegments}>
                  {segments.map(s => s.value > 0 && (
                    <span
                      key={s.key}
                      className={styles.ftSegment}
                      style={{ width: `${(s.value / total) * 100}%`, background: STATUS_KINDS[s.kind].dot }}
                    />
                  ))}
                </div>
              </div>
              <span className={styles.povCount}>
                <strong>{counts.done || 0}</strong> of {total} clusters upgraded
              </span>
            </div>
          )}
        </div>
        <div className={styles.povRightCol}>
          <Pill status={rollout.status} dot={false}>{statusLabel(rollout.status)}</Pill>
          {build.free_tier_rollout_url && !startFailed && (
            <a href={build.free_tier_rollout_url} target="_blank" rel="noreferrer" className={styles.povViewBtn}>
              Open rollout <Ico d={ICO_EXTERNAL} size={12} />
            </a>
          )}
        </div>
      </div>
      {(hasCounts && total > 0) || note ? (
        <div className={styles.ftBody}>
          {hasCounts && total > 0 && (
            <dl className={styles.ftLegend}>
              {segments.map(s => (
                <div key={s.key} className={s.value ? styles.ftLegendItem : styles.ftLegendItemZero}>
                  <span className={styles.ftDot} style={{ background: STATUS_KINDS[s.kind].dot }} />
                  <dt>{s.label}</dt>
                  <dd>{s.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {note}
        </div>
      ) : null}
    </div>
  );
}
